import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ShieldAlert, Eye, EyeOff, Trash2, Music, Hammer, Users, Flag } from "lucide-react";

export const ModerationManager = () => {
  const { t } = useLang();
  const qc = useQueryClient();
  const { data: dir = [] } = useQuery({ queryKey: ["adminDirectory"], queryFn: async () => (await api.get("/admin/directory")).data });
  const { data: groups = [] } = useQuery({ queryKey: ["adminGroups"], queryFn: async () => (await api.get("/admin/groups")).data });
  const { data: reports = [] } = useQuery({ queryKey: ["adminReports"], queryFn: async () => (await api.get("/admin/reports")).data });

  const toggleDir = async (s) => { try { await api.post(`/admin/directory/${s}/toggle`); qc.invalidateQueries({ queryKey: ["adminDirectory"] }); } catch { toast.error("Error"); } };
  const delDir = async (s, n) => { if (!window.confirm(n)) return; try { await api.delete(`/admin/directory/${s}`); qc.invalidateQueries({ queryKey: ["adminDirectory"] }); toast.success(t("mod_deleted")); } catch { toast.error("Error"); } };
  const toggleGrp = async (s) => { try { await api.post(`/admin/groups/${s}/toggle`); qc.invalidateQueries({ queryKey: ["adminGroups"] }); } catch { toast.error("Error"); } };
  const delGrp = async (s, n) => { if (!window.confirm(n)) return; try { await api.delete(`/admin/groups/${s}`); qc.invalidateQueries({ queryKey: ["adminGroups"] }); toast.success(t("mod_deleted")); } catch { toast.error("Error"); } };
  const resolveReport = async (id) => { try { await api.post(`/admin/reports/${id}/resolve`); qc.invalidateQueries({ queryKey: ["adminReports"] }); } catch { toast.error("Error"); } };

  const REPORT_EP = {
    directory: { toggle: (s) => `/admin/directory/${s}/toggle`, del: (s) => `/admin/directory/${s}` },
    group: { toggle: (s) => `/admin/groups/${s}/toggle`, del: (s) => `/admin/groups/${s}` },
    event: { toggle: (s) => `/admin/events/${s}/toggle`, del: (s) => `/admin/events/${s}` },
  };
  const invalidateAll = () => ["adminDirectory", "adminGroups", "adminReports", "events"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const hideTarget = async (r) => {
    const ep = REPORT_EP[r.target_type]; if (!ep) return;
    try { const res = await api.post(ep.toggle(r.target_slug)); toast.success(res.data?.hidden === false ? t("mod_shown_done") : t("mod_hidden_done")); invalidateAll(); } catch { toast.error("Error"); }
  };
  const deleteTarget = async (r) => {
    const ep = REPORT_EP[r.target_type]; if (!ep) return;
    if (!window.confirm(`${t("mod_delete_confirm")} — ${r.target_name}?`)) return;
    try { await api.delete(ep.del(r.target_slug)); await api.post(`/admin/reports/${r.id}/resolve`); toast.success(t("mod_deleted")); invalidateAll(); } catch { toast.error("Error"); }
  };

  const Row = ({ hidden, icon: Icon, title, subtitle, onToggle, onDelete, testid }) => (
    <div data-testid={testid} className={`flex items-center gap-3 p-3 rounded-md border ${hidden ? "border-red-900/40 bg-red-950/10 opacity-70" : "border-[#272A34] bg-[#181A20]"}`}>
      <Icon className="w-4 h-4 text-amber-500 shrink-0" />
      <div className="flex-1 min-w-0"><div className="text-stone-100 truncate">{title}</div><div className="text-xs text-stone-500 truncate">{subtitle}</div></div>
      <button onClick={onToggle} className="text-stone-400 hover:text-amber-400 p-1.5" title={hidden ? t("mod_show") : t("mod_hide")}>{hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
      <button onClick={onDelete} className="text-red-400/70 hover:text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div className="bg-[#121418] border border-[#272A34] rounded-lg p-5 mb-6" data-testid="moderation-manager">
      <div className="flex items-center gap-2 mb-4"><ShieldAlert className="w-5 h-5 text-amber-500" /><h3 className="font-serif text-lg text-amber-100">{t("mod_title")}</h3></div>

      {reports.length > 0 && (
        <div className="mb-6">
          <h4 className="font-mono text-xs uppercase tracking-wider text-red-400/90 mb-2 flex items-center gap-2"><Flag className="w-3.5 h-3.5" /> {t("mod_reports")} ({reports.filter((r) => r.status === "open").length})</h4>
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.id} data-testid={`mod-report-${r.id}`} className={`flex items-center gap-3 p-3 rounded-md border ${r.status === "open" ? "border-red-800/50 bg-red-950/10" : "border-[#272A34] bg-[#181A20] opacity-60"}`}>
                <Flag className="w-4 h-4 text-red-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <Link to={r.target_type === "directory" ? `/directory/${r.target_slug}` : r.target_type === "event" ? `/events/${r.target_slug}` : `/community/${r.target_slug}`} className="text-stone-100 hover:text-amber-400 truncate block">{r.target_name}</Link>
                  <div className="text-xs text-amber-500/70 font-mono uppercase tracking-wider">{r.target_type}</div>
                  {r.reason_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`mod-report-tags-${r.id}`}>
                      {r.reason_tags.map((k) => <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-red-950/40 text-red-300 border border-red-800/40">{t(k)}</span>)}
                    </div>
                  )}
                  <div className="text-xs text-stone-500 truncate">{r.reason || "—"}</div>
                </div>
                {r.status === "open" ? (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button data-testid={`mod-report-hide-${r.id}`} onClick={() => hideTarget(r)} title={t("mod_hide_action")} className="inline-flex items-center gap-1 text-xs bg-[#22252D] hover:bg-[#2c2f39] text-amber-300 px-2.5 py-1.5 rounded-md"><EyeOff className="w-3.5 h-3.5" /></button>
                    <button data-testid={`mod-report-delete-${r.id}`} onClick={() => deleteTarget(r)} title={t("mod_delete_action")} className="inline-flex items-center gap-1 text-xs bg-red-900/70 hover:bg-red-800 text-white px-2.5 py-1.5 rounded-md"><Trash2 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`mod-report-resolve-${r.id}`} onClick={() => resolveReport(r.id)} className="text-xs bg-emerald-800/60 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md">{t("mod_resolve")}</button>
                  </div>
                ) : (
                  <span className="text-xs text-emerald-500 font-mono shrink-0">✓</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <h4 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-2 flex items-center gap-2"><Hammer className="w-3.5 h-3.5" /> {t("mod_directory")} ({dir.length})</h4>
      <div className="space-y-2 mb-6">
        {dir.map((e) => (
          <Row key={e.slug} testid={`mod-dir-${e.slug}`} hidden={e.hidden} icon={e.type === "band" ? Music : Hammer} title={e.name} subtitle={`${e.craft || ""} · ${e.region || ""}`} onToggle={() => toggleDir(e.slug)} onDelete={() => delDir(e.slug, e.name)} />
        ))}
      </div>

      <h4 className="font-mono text-xs uppercase tracking-wider text-amber-500/90 mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {t("mod_groups")} ({groups.length})</h4>
      <div className="space-y-2">
        {groups.map((g) => (
          <Row key={g.slug} testid={`mod-grp-${g.slug}`} hidden={g.hidden} icon={Users} title={g.name} subtitle={`${g.member_count || 0} membres · ${g.post_count || 0} posts`} onToggle={() => toggleGrp(g.slug)} onDelete={() => delGrp(g.slug, g.name)} />
        ))}
      </div>
    </div>
  );
};

export default ModerationManager;
